import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowDownCircle, ArrowUpCircle, Wallet as WalletIcon, Clock, Check, X as XIcon, Copy, QrCode } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Wallet() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [pendings, setPendings] = useState([]);
  const [busy, setBusy] = useState(null);
  const [showQr, setShowQr] = useState(false);

  const refresh = () => {
    api.get("/cashback/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/cashback/transactions").then((r) => setTxs(r.data)).catch(() => {});
    api.get("/redemptions/pending").then((r) => setPendings(r.data)).catch(() => {});
  };

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 15000);
    return () => clearInterval(i);
  }, []);

  const approve = async (rid) => {
    setBusy(rid);
    try {
      const { data } = await api.post(`/redemptions/${rid}/approve`);
      toast.success(`Aprovado! Novo saldo: B$ ${data.remaining_balance.toFixed(2)}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const reject = async (rid) => {
    setBusy(rid);
    try { await api.post(`/redemptions/${rid}/reject`); toast.success("Solicitação rejeitada"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const copyCode = () => {
    if (!user?.wallet_code) return;
    navigator.clipboard.writeText(user.wallet_code);
    toast.success("Código copiado!");
  };

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-12">
      <h1 className="font-display text-2xl sm:text-4xl font-bold text-stone-900">Minha Carteira</h1>
      <p className="text-stone-500 mt-0.5 text-xs sm:text-base">Mostre seu código ou QR Code ao comerciante para receber e usar seu cashback.</p>

      <div className="mt-4 sm:mt-8 grid grid-cols-1 sm:grid-cols-5 gap-3 sm:gap-4">
        <Card className="sm:col-span-3 p-4 sm:p-8 rounded-3xl bg-gradient-to-br from-blue-900 to-blue-950 text-white shadow-xl border-0" data-testid="wallet-balance-card">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <span className="text-[10px] sm:text-xs uppercase tracking-widest text-blue-200 font-semibold">Saldo disponível</span>
              <div className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold mt-1 sm:mt-2 tracking-tight" data-testid="wallet-balance">
                B$ {(wallet?.balance || 0).toFixed(2).replace(".", ",")}
              </div>
              <p className="text-[10px] sm:text-xs text-blue-100 mt-1 sm:mt-2 truncate">Carteira global · {user?.email}</p>
            </div>
            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
              <WalletIcon size={20}/>
            </div>
          </div>
        </Card>

        <Card className="sm:col-span-2 p-3 sm:p-6 rounded-3xl bg-white border-2 border-amber-300 shadow-lg flex flex-col items-center justify-center text-center" data-testid="wallet-code-card">
          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Código da Carteira</span>
          <div className="font-mono text-lg sm:text-2xl font-extrabold text-blue-950 mt-1 tracking-wider" data-testid="wallet-code">
            {user?.wallet_code || "—"}
          </div>
          <div className="mt-2 sm:mt-3 flex gap-1.5 sm:gap-2">
            <Button size="sm" variant="outline" onClick={copyCode} className="rounded-lg text-[11px] sm:text-xs h-8" data-testid="copy-wallet-code">
              <Copy size={12} className="mr-1"/> Copiar
            </Button>
            <Button size="sm" onClick={() => setShowQr(true)} className="rounded-lg text-[11px] sm:text-xs h-8 bg-blue-900 hover:bg-blue-950 text-white" data-testid="show-qr-btn">
              <QrCode size={12} className="mr-1"/> QR
            </Button>
          </div>
        </Card>
      </div>

      {pendings.length > 0 && (
        <div className="mt-6 sm:mt-8">
          <h2 className="font-display text-base sm:text-xl font-semibold text-stone-900 mb-3 sm:mb-4 flex items-center gap-2">
            <Clock className="text-amber-600" size={18}/> Aprovações pendentes ({pendings.length})
          </h2>
          <div className="space-y-2 sm:space-y-3">
            {pendings.map((r) => (
              <Card key={r.id} className="p-3 sm:p-4 rounded-2xl border-amber-300 bg-amber-50 border-2" data-testid={`pending-redemption-${r.id}`}>
                <div className="flex items-start justify-between gap-2 sm:gap-3 flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-xs sm:text-sm font-bold text-stone-900">{r.company_name}</div>
                    <div className="text-[11px] sm:text-xs text-stone-700 mt-0.5">{r.items_description}</div>
                    <div className="text-[10px] sm:text-[11px] text-stone-500 mt-1">{new Date(r.created_at).toLocaleString("pt-BR")} · B$ {r.items_value.toFixed(2)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Solicitado</div>
                    <div className="font-display text-xl sm:text-2xl font-bold text-amber-900">B$ {r.cashback_to_use.toFixed(2).replace(".", ",")}</div>
                  </div>
                </div>
                <div className="flex gap-2 mt-2 sm:mt-3 justify-end">
                  <Button size="sm" variant="outline" onClick={() => reject(r.id)} disabled={busy === r.id} className="rounded-lg h-8 text-xs" data-testid={`reject-${r.id}`}>
                    <XIcon size={13} className="mr-1"/> Rejeitar
                  </Button>
                  <Button size="sm" onClick={() => approve(r.id)} disabled={busy === r.id} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg h-8 text-xs" data-testid={`approve-${r.id}`}>
                    <Check size={13} className="mr-1"/> Aprovar
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 sm:mt-10">
        <h2 className="font-display text-base sm:text-xl font-semibold text-stone-900 mb-3 sm:mb-4">Histórico</h2>
        <Card className="rounded-2xl border-stone-200 overflow-hidden">
          {txs.length === 0 && (
            <p className="text-xs sm:text-sm text-stone-500 text-center p-6 sm:p-8" data-testid="wallet-empty">Nenhuma transação ainda. Compre em estabelecimentos Prata ou Ouro para receber cashback.</p>
          )}
          {txs.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-5 py-3 sm:py-4 border-b border-stone-100 last:border-0">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${t.type === "CREDIT" ? "bg-blue-100 text-blue-900" : "bg-stone-100 text-stone-600"}`}>
                  {t.type === "CREDIT" ? <ArrowUpCircle size={18}/> : <ArrowDownCircle size={18}/>}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-xs sm:text-sm text-stone-900 truncate">{t.company_name}</div>
                  <div className="text-[10px] sm:text-[11px] text-stone-500">{new Date(t.created_at).toLocaleString("pt-BR")} · Compra B$ {t.purchase_value.toFixed(2)}</div>
                </div>
              </div>
              <div className={`font-display font-bold text-sm sm:text-lg shrink-0 ${t.type === "CREDIT" ? "text-blue-900" : "text-stone-700"}`}>
                {t.type === "CREDIT" ? "+" : "−"} B$ {t.cashback_value.toFixed(2)}
              </div>
            </div>
          ))}
        </Card>
      </div>

      {showQr && user?.wallet_code && (
        <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4" onClick={() => setShowQr(false)} data-testid="wallet-qr-modal">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm w-full text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Mostre ao comerciante</span>
            <div className="font-mono text-2xl font-extrabold text-blue-950 mt-1 tracking-wider">{user.wallet_code}</div>
            <div className="mt-5 p-4 bg-white rounded-2xl border-4 border-blue-900 inline-block">
              <QRCodeSVG value={user.wallet_code} size={220} level="H" includeMargin={false}/>
            </div>
            <p className="text-xs text-stone-500 mt-4">O comerciante escaneia este QR para identificar sua carteira ao conceder ou resgatar cashback.</p>
            <Button onClick={() => setShowQr(false)} variant="outline" className="mt-5 rounded-xl" data-testid="close-qr-modal">Fechar</Button>
          </div>
        </div>
      )}
    </div>
  );
}
